<div [formGroup]="formGroup">

  <!-- ===================================================== identity (RF-09, RF-10) -->
  <div class="row">
    <div class="col-12 col-md-7 mb-3">
      <label class="form-label" for="template-name">
        {{ 'channel.templateBuilder.nameLabel' | translate }}
        <span class="text-danger">*</span>
      </label>
      <input id="template-name" type="text" class="form-control" formControlName="name"
        [class.is-invalid]="nameRejected" [maxlength]="limits.NAME_MAX_LENGTH" [readonly]="editing"
        [attr.aria-describedby]="nameRejected ? 'template-name-error' : null"
        [attr.aria-invalid]="nameRejected"
        [placeholder]="'channel.templateBuilder.namePlaceholder' | translate" />
      <div class="d-flex justify-content-between gap-2">
        <span class="form-text small">{{ 'channel.templateBuilder.nameHelp' | translate }}</span>
        <span class="form-text small text-nowrap">{{ nameLength }}/{{ limits.NAME_MAX_LENGTH }}</span>
      </div>
      @if (nameRejected) {
      <span id="template-name-error" role="alert" class="form-text small text-danger">
        {{ 'channel.templateBuilder.nameInvalid' | translate }}
      </span>
      }
      @if (isMissing('name')) {
      <span role="alert" class="form-text small text-danger">
        {{ 'channel.templateBuilder.fieldRequired' | translate }}
      </span>
      }
    </div>

    <div class="col-12 col-md-5 mb-3">
      <label class="form-label" for="template-language">
        {{ 'channel.templateBuilder.languageLabel' | translate }}
        <span class="text-danger">*</span>
      </label>
      <!-- Language names come from the provider and stay in its wording — they are not UI chrome. -->
      <mat-select id="template-language" class="form-control" formControlName="language">
        @for (language of languages; track language.code) {
        <mat-option [value]="language.code">{{ language.name }} ({{ language.code }})</mat-option>
        }
      </mat-select>
    </div>
  </div>

  <hr />

  <!-- ==================================================== variable type (RF-11, RF-14) -->
  <div class="mb-3">
    <label class="form-label" for="template-parameter-format">
      {{ 'channel.templateBuilder.variableTypeLabel' | translate }}
    </label>
    <!-- (selectionChange) emits the option's real value; reading the DOM here is what sent Meta
         "0: POSITIONAL" instead of "POSITIONAL". -->
    <mat-select id="template-parameter-format" class="form-control" formControlName="parameterFormat"
      (selectionChange)="onParameterFormatChange($event.value)">
      @for (format of parameterFormats; track format.value) {
      <mat-option [value]="format.value">{{ format.label | translate }}</mat-option>
      }
    </mat-select>
    <span class="form-text small">{{ 'channel.templateBuilder.variableTypeHelp' | translate }}</span>
  </div>

  <!-- ================================================ media sample and header (RF-15 … RF-18) -->
  <div class="mb-3">
    <label class="form-label" for="template-header-format">
      {{ 'channel.templateBuilder.mediaSampleLabel' | translate }}
      <span class="text-muted">({{ 'channel.templateBuilder.optional' | translate | lowercase }})</span>
    </label>
    <mat-select id="template-header-format" class="form-control" formControlName="headerFormat">
      @for (format of headerFormats; track format.value) {
      <mat-option [value]="format.value">{{ format.label | translate }}</mat-option>
      }
    </mat-select>
  </div>

  @if (engine.isMediaHeader()) {
  <div class="mb-3">
    <label class="form-label" for="template-media">{{ 'channel.templateBuilder.mediaFileLabel' | translate }}</label>

    <!-- Upload area, as the eleven other upload screens present it — but the drop handlers are real
         here: those screens all say "or drag and drop" without ever listening for a drop. -->
    <div class="template-upload-area border-dashed rounded text-center p-3" [class.is-dragging]="dragging"
      (dragover)="onDragOver($event)" (dragleave)="onDragLeave($event)" (drop)="onDrop($event)">

      @if (uploadingMedia) {
      <loading-component diameter="28" />
      <span class="d-block text-muted small">{{ 'channel.templateBuilder.mediaUploading' | translate }}</span>
      } @else if (engine.mediaFileName || engine.currentMediaUrl) {
      <div class="d-flex align-items-center justify-content-center gap-2">
        <i class="ri-attachment-2 text-muted"></i>
        <span class="text-truncate">
          {{ engine.mediaFileName || ('channel.templateBuilder.mediaAlreadyAttached' | translate) }}
        </span>
        <button type="button" class="btn btn-sm btn-soft-danger" (click)="removeMedia()">
          {{ 'channel.templateBuilder.mediaRemove' | translate }}
        </button>
      </div>
      } @else {
      <label class="template-upload-label d-block w-100 mb-0" for="template-media">
        <i class="ri-upload-cloud-2-line d-block template-upload-icon text-muted"></i>
        <span class="d-block text-primary">{{ 'channel.templateBuilder.mediaClickToUpload' | translate }}</span>
        <span class="d-block text-muted small">{{ 'channel.templateBuilder.mediaOrDragAndDrop' | translate }}</span>
        <span class="d-block text-muted small">
          {{ 'channel.templateBuilder.mediaAccepted' | translate:{ formats: mediaAccept } }}
        </span>
      </label>
      <input id="template-media" type="file" class="d-none" [accept]="mediaAccept" (change)="onFileSelected($event)" />
      }
    </div>
  </div>
  }

  <div class="mb-3">
    <label class="form-label" for="template-header-text">
      {{ 'channel.templateBuilder.headerLabel' | translate }}
      <span class="text-muted">({{ 'channel.templateBuilder.optional' | translate | lowercase }})</span>
    </label>
    <div class="d-flex gap-2">
      <input #headerInput id="template-header-text" type="text" class="form-control" formControlName="headerText"
        [maxlength]="limits.HEADER_TEXT_MAX_LENGTH" (keyup)="rememberCaret('headerText', headerInput)"
        (click)="rememberCaret('headerText', headerInput)" />
      <!-- RF-18 — the action disappears once the header already holds one variable. -->
      @if (engine.canInsertHeaderParameter) {
      <button type="button" class="btn btn-light text-nowrap" (click)="insertHeaderParameter(headerInput)">
        {{ 'channel.templateBuilder.addVariable' | translate }}
      </button>
      }
    </div>
    <div class="d-flex justify-content-between gap-2">
      <span class="form-text small">{{ 'channel.templateBuilder.headerHelp' | translate }}</span>
      <span class="form-text small text-nowrap">{{ length('headerText') }}/{{ limits.HEADER_TEXT_MAX_LENGTH }}</span>
    </div>
  </div>

  <!-- ========================================================== body (RF-19, RF-20) -->
  <div class="mb-3">
    <label class="form-label" for="template-body">
      {{ 'channel.templateBuilder.bodyLabel' | translate }}
      <span class="text-danger">*</span>
    </label>

    <textarea #bodyInput id="template-body" class="form-control" rows="8" formControlName="body"
      [maxlength]="limits.BODY_TEXT_MAX_LENGTH" (keyup)="rememberCaret('body', bodyInput)"
      (click)="rememberCaret('body', bodyInput)"></textarea>

    <!--
      Toolbar below the field and right-aligned, as the campaign message editor does it. The icons
      stay real <button> elements rather than bare <i>: the house pattern is not keyboard reachable,
      and RNF-07 asks for the whole flow to be. `btn-link p-0` makes them read as plain icons.
    -->
    <div class="d-flex align-items-center justify-content-end gap-2 flex-wrap mt-2" role="toolbar"
      [attr.aria-label]="'channel.templateBuilder.formattingToolbar' | translate">
      <button type="button" class="btn btn-link p-0 fs-4 text-body text-decoration-none"
        (click)="applyStyle(bodyInput, 'BOLD')"
        [attr.aria-label]="'channel.templateBuilder.styleBold' | translate"
        [matTooltip]="'channel.templateBuilder.styleBold' | translate"><i class="ri-bold"></i></button>

      <button type="button" class="btn btn-link p-0 fs-4 text-body text-decoration-none"
        (click)="applyStyle(bodyInput, 'ITALIC')"
        [attr.aria-label]="'channel.templateBuilder.styleItalic' | translate"
        [matTooltip]="'channel.templateBuilder.styleItalic' | translate"><i class="ri-italic"></i></button>

      <button type="button" class="btn btn-link p-0 fs-4 text-body text-decoration-none"
        (click)="applyStyle(bodyInput, 'STRIKETHROUGH')"
        [attr.aria-label]="'channel.templateBuilder.styleStrikethrough' | translate"
        [matTooltip]="'channel.templateBuilder.styleStrikethrough' | translate"><i class="ri-strikethrough"></i></button>

      <button type="button" class="btn btn-link p-0 fs-4 text-body text-decoration-none"
        (click)="applyStyle(bodyInput, 'MONOSPACE')"
        [attr.aria-label]="'channel.templateBuilder.styleMonospace' | translate"
        [matTooltip]="'channel.templateBuilder.styleMonospace' | translate"><i class="ri-code-line"></i></button>

      <!-- The picker floats over the content instead of pushing the field down. -->
      <div class="position-relative">
        <button type="button" class="btn btn-link p-0 fs-4 text-body text-decoration-none"
          (click)="toggleEmojiPicker($event)"
          [attr.aria-label]="'channel.templateBuilder.emoji' | translate"
          [matTooltip]="'channel.templateBuilder.emoji' | translate"><i class="ri-emotion-line"></i></button>

        @if (showEmojiPicker) {
        <div class="position-absolute end-0 mt-2 z-3" (click)="$event.stopPropagation()">
          <emoji-mart [i18n]="$any(emojiTranslator)" [darkMode]="false"
            (emojiSelect)="addEmoji($any($event), bodyInput)" />
        </div>
        }
      </div>

      <button type="button" class="btn btn-link p-0 text-body text-decoration-none"
        (click)="insertBodyParameter(bodyInput)">
        {{ 'channel.templateBuilder.addVariable' | translate }}
      </button>
    </div>

    <div class="d-flex justify-content-end">
      <span class="form-text small text-nowrap">{{ length('body') }}/{{ limits.BODY_TEXT_MAX_LENGTH }}</span>
    </div>
    @if (isMissing('body')) {
    <span class="form-text small text-danger" role="alert">
      {{ 'channel.templateBuilder.fieldRequired' | translate }}
    </span>
    }
  </div>

  <!-- ============================================================== footer (RF-21) -->
  <div class="mb-3">
    <label class="form-label" for="template-footer">
      {{ 'channel.templateBuilder.footerLabel' | translate }}
      <span class="text-muted">({{ 'channel.templateBuilder.optional' | translate | lowercase }})</span>
    </label>
    <!-- Always editable, whatever the media sample is, and it offers no insert-variable action. -->
    <input id="template-footer" type="text" class="form-control" formControlName="footer"
      [maxlength]="limits.FOOTER_TEXT_MAX_LENGTH" />
    <div class="d-flex justify-content-between gap-2">
      <span class="form-text small">{{ 'channel.templateBuilder.footerHelp' | translate }}</span>
      <span class="form-text small text-nowrap">{{ length('footer') }}/{{ limits.FOOTER_TEXT_MAX_LENGTH }}</span>
    </div>
  </div>

  <hr />

  <!-- ============================================ buttons (RF-22, RF-35 … RF-50) -->
  <div class="mb-3">
    <h5 class="mb-1">
      {{ 'channel.templateBuilder.buttonsTitle' | translate }}
      <span class="text-muted fw-normal">· {{ 'channel.templateBuilder.optional' | translate }}</span>
    </h5>
    <p class="text-muted font-13">{{ 'channel.templateBuilder.buttonsDescription' | translate }}</p>

    <!-- The description already states the maximum, so no counter here. The action stays under the
         text and never moves, whether the template has no buttons or ten. -->
    <!-- Same variant as "Novo template" on the list screen next door, which is this module's own
         precedent for an add action. -->
    <button type="button" class="btn btn-primary fw-bold" [matMenuTriggerFor]="addButtonMenu"
      [disabled]="!canAddAnyButton">
      <i class="ri-add-line"></i> {{ 'channel.templateBuilder.addButton' | translate }}
      <i class="ri-arrow-down-s-line"></i>
    </button>

    @if (callToActionIndexes.length > 0) {
    <h6 class="text-uppercase text-muted font-12 mt-3">
      {{ 'channel.templateBuilder.buttonFamilyCallToAction' | translate }}
      <span class="fw-normal">· {{ 'channel.templateBuilder.optional' | translate }}</span>
    </h6>
    <div cdkDropList (cdkDropListDropped)="dropButton($any($event), callToActionIndexes)">
      @for (index of callToActionIndexes; track index; let position = $index) {
      <div cdkDrag class="border rounded p-2 mb-2">
        <template-button-row [engine]="engine" [index]="index" [indexes]="callToActionIndexes" [position]="position"
          (removed)="removeButton($event)" />
      </div>
      }
    </div>
    }

    @if (quickReplyIndexes.length > 0) {
    <h6 class="text-uppercase text-muted font-12 mt-3">
      {{ 'channel.templateBuilder.buttonFamilyQuickReply' | translate }}
      <span class="fw-normal">· {{ 'channel.templateBuilder.optional' | translate }}</span>
    </h6>
    <div cdkDropList (cdkDropListDropped)="dropButton($any($event), quickReplyIndexes)">
      @for (index of quickReplyIndexes; track index; let position = $index) {
      <div cdkDrag class="border rounded p-2 mb-2">
        <template-button-row [engine]="engine" [index]="index" [indexes]="quickReplyIndexes" [position]="position"
          (removed)="removeButton($event)" />
      </div>
      }
    </div>
    }

    <mat-menu #addButtonMenu="matMenu">
      @for (type of buttonTypes; track type.value) {
      <button type="button" mat-menu-item [disabled]="!engine.canAddButton(type.value)"
        (click)="addButton(type.value)">
        {{ type.label | translate }}
        @if (!engine.canAddButton(type.value)) {
        <span class="text-muted font-12">
          @if (engine.conflictsWithCallButton(type.value)) {
          ({{ 'channel.templateBuilder.buttonSingleCallButton' | translate }})
          } @else {
          ({{ 'channel.templateBuilder.buttonTypeAtLimit' | translate }})
          }
        </span>
        }
      </button>
      }
    </mat-menu>
  </div>

  <!-- ==================================================== variable samples (RF-23 … RF-25, RF-48) -->
  @if (hasSamples) {
  <hr />
  <div class="mb-3">
    <h5 class="mb-1">{{ 'channel.templateBuilder.samplesTitle' | translate }}</h5>
    <p class="text-muted font-13">{{ 'channel.templateBuilder.samplesWarning' | translate }}</p>

    @if (engine.headerParameters.length > 0) {
    <h6 class="text-uppercase text-muted font-12">{{ 'channel.templateBuilder.samplesHeaderGroup' | translate }}</h6>
    <!--
      Bound to the CONTROL OBJECT, not to its position in the array.
      `formControlName="i"` resolves its control exactly once — when the directive's own input
      changes — and the index never changes while the array is rebuilt. So any code that replaces
      the instance sitting at a live index leaves the input writing to a control that is no longer
      in the form: the typed text stays on screen while the array's control is still empty and
      required, which reads as "this filled field says it is missing".
      `[formControl]` takes the instance itself, so the binding cannot outlive the control it points
      at — the input and the control the form validates are the same object by construction.
    -->
    <div>
      @for (control of headerExamples.controls; track control; let i = $index) {
      <div class="mb-2">
        <label class="form-label font-13" [for]="'sample-header-' + i">{{ parameterLabel(engine.headerParameters[i]) }}</label>
        <input [id]="'sample-header-' + i" type="text" class="form-control" [formControl]="$any(control)"
          [class.is-invalid]="control.invalid && control.touched"
          [attr.aria-describedby]="control.invalid && control.touched ? 'sample-header-error-' + i : null" />
        @if (control.invalid && control.touched) {
        <span [id]="'sample-header-error-' + i" role="alert" class="form-text small text-danger">
          {{ 'channel.templateBuilder.sampleRequired' | translate }}
        </span>
        }
      </div>
      }
    </div>
    }

    @if (engine.bodyParameters.length > 0) {
    <h6 class="text-uppercase text-muted font-12">{{ 'channel.templateBuilder.samplesBodyGroup' | translate }}</h6>
    <!-- Bound to the control object — see the header samples above for why the index is not it. -->
    <div>
      @for (control of bodyExamples.controls; track control; let i = $index) {
      <div class="mb-2">
        <label class="form-label font-13" [for]="'sample-body-' + i">{{ parameterLabel(engine.bodyParameters[i]) }}</label>
        <input [id]="'sample-body-' + i" type="text" class="form-control" [formControl]="$any(control)"
          [class.is-invalid]="control.invalid && control.touched"
          [attr.aria-describedby]="control.invalid && control.touched ? 'sample-body-error-' + i : null" />
        @if (control.invalid && control.touched) {
        <span [id]="'sample-body-error-' + i" role="alert" class="form-text small text-danger">
          {{ 'channel.templateBuilder.sampleRequired' | translate }}
        </span>
        }
      </div>
      }
    </div>
    }

    <!-- RF-48 — a Dynamic URL button carries one variable of its own. -->
    @if (engine.dynamicUrlButtonIndexes.length > 0) {
    <h6 class="text-uppercase text-muted font-12">{{ 'channel.templateBuilder.samplesButtonGroup' | translate }}</h6>
    <div formArrayName="buttons">
      @for (index of engine.dynamicUrlButtonIndexes; track index) {
      <div class="mb-2" [formGroupName]="index">
        <label class="form-label font-13" [for]="'sample-url-' + index">
          {{ buttonGroup(index).get('text').value || ('channel.templateBuilder.buttonUrl' | translate) }}
        </label>
        <input [id]="'sample-url-' + index" type="text" class="form-control" formControlName="urlExample"
          [placeholder]="'channel.templateBuilder.sampleUrlPlaceholder' | translate" />
      </div>
      }
    </div>
    }
  </div>
  }

</div>

<!-- =============================================================== one button row -->
